import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { EyeIcon, SparklesIcon, PackageIcon, ShieldIcon, ClockIcon, CheckIcon, ArrowRightIcon, StarIcon, LockIcon, VideoIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { useCart } from '../contexts/CartContext';
type ServiceType = 'natal' | 'solar' | 'compatibility' | 'combo' | 'forecast' | 'consultation';
interface ServicePurchaseSectionProps {
  serviceType: ServiceType;
  onNavigate: (page: string) => void;
}
const SERVICE_CONFIG: Record<ServiceType, {
  name: string;
  betaPrice: string;
  premiumPrice: string;
  betaFeatures: string[];
  premiumFeatures: string[];
  betaPages: string;
  premiumPages: string;
  color: string;
  demoPage: string;
}> = {
  natal: {
    name: 'Натальная карта',
    betaPrice: 'Бесплатно',
    premiumPrice: '3 900 ₽',
    betaFeatures: ['Анализ Солнца, Луны и Асцендента', 'Основные черты характера', 'Общие рекомендации'],
    premiumFeatures: ['Всё из Beta отчёта', 'Все планеты в знаках и домах', 'Аспекты между планетами', 'Карьера, финансы, отношения', 'Кармические задачи и узлы', 'Компенсаторика и рекомендации'],
    betaPages: '1-3 страницы',
    premiumPages: '30-40 страниц',
    color: 'indigo',
    demoPage: 'report-preview-natal'
  },
  solar: {
    name: 'Соляр',
    betaPrice: 'Бесплатно',
    premiumPrice: '4 400 ₽',
    betaFeatures: ['Общая тема и фокус года', 'Основные планетарные влияния', 'Краткий прогноз по сферам'],
    premiumFeatures: ['Всё из Beta отчёта', 'Все планеты в домах соляра', 'Аспекты солярной карты', 'Помесячный прогноз', 'Карьера, финансы, отношения', 'Зоны риска и рекомендации'],
    betaPages: '1-3 страницы',
    premiumPages: '25-35 страниц',
    color: 'amber',
    demoPage: 'report-preview-solar'
  },
  compatibility: {
    name: 'Совместимость',
    betaPrice: 'Бесплатно',
    premiumPrice: '4 900 ₽',
    betaFeatures: ['Общий анализ совместимости', 'Взаимодействие Солнца и Луны', 'Основные сильные стороны пары'],
    premiumFeatures: ['Всё из Beta отчёта', 'Взаимодействие Венеры и Марса', 'Все аспекты между картами', 'Эмоциональная и сексуальная совместимость', 'Кармические связи', 'Практические рекомендации для пары'],
    betaPages: '1-3 страницы',
    premiumPages: '30-40 страниц',
    color: 'rose',
    demoPage: 'report-preview-compatibility'
  },
  combo: {
    name: 'Комбо: Натал + Соляр',
    betaPrice: 'Бесплатно',
    premiumPrice: '6 900 ₽',
    betaFeatures: ['Краткий профиль личности', 'Краткий прогноз года', 'Основные рекомендации'],
    premiumFeatures: ['Полный анализ натальной карты', 'Полный соляр на год', 'Все планеты и аспекты', 'Карьера, финансы, отношения', 'Помесячный прогноз', 'Компенсаторика и рекомендации'],
    betaPages: '1-3 страницы',
    premiumPages: '50-60 страниц',
    color: 'violet',
    demoPage: 'report-preview-combo'
  },
  forecast: {
    name: 'Прогноз (Транзиты)',
    betaPrice: 'Бесплатно',
    premiumPrice: '3 900 ₽',
    betaFeatures: ['Основной транзит периода', 'Краткий прогноз', 'Общие рекомендации'],
    premiumFeatures: ['Всё из Beta отчёта', 'Полный анализ транзитов', 'Ключевые даты и периоды', 'Карьера, финансы, отношения', 'Зоны риска', 'Практические рекомендации'],
    betaPages: '1-3 страницы',
    premiumPages: '20-30 страниц',
    color: 'teal',
    demoPage: 'report-preview-forecast'
  },
  consultation: {
    name: 'Консультация',
    betaPrice: '',
    premiumPrice: 'от 2 990 ₽',
    betaFeatures: [],
    premiumFeatures: ['Живое общение с астрологом', 'Персональный разбор карты', 'Ответы на ваши вопросы', 'Видеозапись встречи', 'PDF-итоги консультации'],
    betaPages: '',
    premiumPages: '60-90 минут',
    color: 'sky',
    demoPage: 'astrologers'
  }
};
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
export function ServicePurchaseSection({
  serviceType,
  onNavigate
}: ServicePurchaseSectionProps) {
  const config = SERVICE_CONFIG[serviceType];
  const isConsultation = serviceType === 'consultation';
  const {
    addItem
  } = useCart();
  const handleBuyPremium = () => {
    const priceNum = parseInt(config.premiumPrice.replace(/[^\d]/g, ''));
    addItem({
      id: `premium-${serviceType}-${Date.now()}`,
      name: `Premium отчёт: ${config.name}`,
      description: 'Полный профессиональный отчёт с глубоким анализом и практическими рекомендациями.',
      price: priceNum,
      type: 'premium-report',
      serviceName: config.name,
      format: 'PDF-отчёт',
      duration: config.premiumPages,
      badges: [{
        label: 'Premium',
        variant: 'pro'
      }]
    });
    onNavigate('checkout');
  };
  const handleBuyPackage = () => {
    addItem({
      id: `pkg-20-${Date.now()}`,
      name: 'Пакет 20 отчётов',
      description: 'Купите пакет отчётов со скидкой и используйте для любых услуг платформы.',
      price: 19900,
      type: 'package',
      format: 'PDF-отчёты',
      duration: 'Бессрочно',
      badges: [{
        label: 'Пакет отчётов',
        variant: 'success'
      }]
    });
    onNavigate('checkout');
  };
  const colorClasses: Record<string, {
    bg: string;
    text: string;
    ring: string;
    badge: string;
  }> = {
    indigo: {
      bg: 'bg-indigo-50',
      text: 'text-indigo-600',
      ring: 'ring-indigo-100 border-indigo-200',
      badge: 'bg-indigo-600'
    },
    amber: {
      bg: 'bg-amber-50',
      text: 'text-amber-600',
      ring: 'ring-amber-100 border-amber-200',
      badge: 'bg-amber-500'
    },
    rose: {
      bg: 'bg-rose-50',
      text: 'text-rose-600',
      ring: 'ring-rose-100 border-rose-200',
      badge: 'bg-rose-500'
    },
    violet: {
      bg: 'bg-violet-50',
      text: 'text-violet-600',
      ring: 'ring-violet-100 border-violet-200',
      badge: 'bg-violet-600'
    },
    teal: {
      bg: 'bg-teal-50',
      text: 'text-teal-600',
      ring: 'ring-teal-100 border-teal-200',
      badge: 'bg-teal-600'
    },
    sky: {
      bg: 'bg-sky-50',
      text: 'text-sky-600',
      ring: 'ring-sky-100 border-sky-200',
      badge: 'bg-sky-600'
    }
  };
  const colors = colorClasses[config.color];
  if (isConsultation) {
    return <div className="py-14 sm:py-24 bg-white" data-id="element-17715">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-17716">
          <div className="text-center mb-8 sm:mb-16" data-id="element-17717">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-17718">
              Запишитесь на консультацию
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-17719">
              Выберите астролога и формат консультации
            </p>
          </div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-17720">
            {/* Consultation Card */}
            <motion.div variants={staggerItem} data-id="element-17721">
              <Card padding="lg" className={`flex flex-col h-full ${colors.ring} ring-1 shadow-xl relative`} data-id="element-17722">
                <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 z-10 ${colors.badge} text-white text-xs font-bold px-4 py-1 rounded-full`} data-id="element-17723">
                  Рекомендуем
                </div>
                <div className="flex items-center gap-3 mb-4" data-id="element-17724">
                  <div className={`w-12 h-12 rounded-xl ${colors.bg} ${colors.text} flex items-center justify-center`} data-id="element-17725">
                    <VideoIcon className="w-6 h-6" data-id="element-17726" />
                  </div>
                  <div data-id="element-17727">
                    <Badge variant="pro" className="mb-1" data-id="element-17728">
                      Живое общение
                    </Badge>
                    <h3 className="text-xl font-bold text-slate-900" data-id="element-17729">
                      Консультация с астрологом
                    </h3>
                  </div>
                </div>
                <p className="text-slate-500 mb-6 flex-grow" data-id="element-17730">
                  Персональный разбор вашей карты с профессиональным астрологом.
                  Ответы на все ваши вопросы.
                </p>
                <ul className="space-y-3 mb-6" data-id="element-17731">
                  {config.premiumFeatures.map((feature, i) => <li key={i} className="flex items-start text-sm text-slate-600" data-id="element-17732">
                      <CheckIcon className="w-4 h-4 text-emerald-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-17733" />
                      {feature}
                    </li>)}
                </ul>
                <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6" data-id="element-17734">
                  {config.premiumPrice}
                </div>
                <Button variant="primary" fullWidth onClick={() => onNavigate('astrologers')} className={`${colors.badge} hover:opacity-90`} data-id="element-17735">
                  Выбрать астролога
                  <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-17736" />
                </Button>
              </Card>
            </motion.div>

            {/* Package Card */}
            <motion.div variants={staggerItem} data-id="element-17737">
              <Card padding="lg" className="flex flex-col h-full border-slate-200" data-id="element-17738">
                <div className="flex items-center gap-3 mb-4" data-id="element-17739">
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center" data-id="element-17740">
                    <PackageIcon className="w-6 h-6" data-id="element-17741" />
                  </div>
                  <div data-id="element-17742">
                    <Badge variant="success" className="mb-1" data-id="element-17743">
                      Выгода до 40%
                    </Badge>
                    <h3 className="text-xl font-bold text-slate-900" data-id="element-17744">
                      Пакеты отчётов
                    </h3>
                  </div>
                </div>
                <p className="text-slate-500 mb-6 flex-grow" data-id="element-17745">
                  Приобретите пакет отчётов со скидкой и используйте их для
                  любых услуг платформы.
                </p>
                <ul className="space-y-3 mb-6" data-id="element-17746">
                  <li className="flex items-start text-sm text-slate-600" data-id="element-17747">
                    <CheckIcon className="w-4 h-4 text-emerald-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-17748" />
                    Пакет 20 отчётов — 19 900 ₽ (≈ 995 ₽/шт)
                  </li>
                  <li className="flex items-start text-sm text-slate-600" data-id="element-17749">
                    <CheckIcon className="w-4 h-4 text-emerald-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-17750" />
                    Пакет 50 отчётов — 44 900 ₽ (≈ 898 ₽/шт)
                  </li>
                  <li className="flex items-start text-sm text-slate-600" data-id="element-17751">
                    <CheckIcon className="w-4 h-4 text-emerald-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-17752" />
                    Пакет 100 отчётов — 79 900 ₽ (≈ 799 ₽/шт)
                  </li>
                </ul>
                <div className="text-lg font-bold text-emerald-600 mb-6" data-id="element-17753">
                  Экономия до 40% от розничной цены
                </div>
                <Button variant="outline" fullWidth onClick={() => onNavigate('pricing')} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-id="element-17754">
                  Выбрать пакет
                  <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-17755" />
                </Button>
              </Card>
            </motion.div>
          </motion.div>

          {/* Trust Badges */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500" data-id="element-17756">
            <span className="flex items-center" data-id="element-17757">
              <ShieldIcon className="w-4 h-4 mr-1.5 text-emerald-500" data-id="element-17758" />
              Безопасная оплата
            </span>
            <span className="flex items-center" data-id="element-17759">
              <ClockIcon className="w-4 h-4 mr-1.5 text-sky-500" data-id="element-17760" />
              Запись на удобное время
            </span>
            <span className="flex items-center" data-id="element-17761">
              <StarIcon className="w-4 h-4 mr-1.5 text-amber-500" data-id="element-17762" />
              Проверенные астрологи
            </span>
          </div>
        </div>
      </div>;
  }
  return <div className="py-14 sm:py-24 bg-white" data-id="element-17763">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-17764">
        <div className="text-center mb-8 sm:mb-16" data-id="element-17765">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-17766">
            Выберите формат отчёта
          </h2>
          <p className="text-base sm:text-lg text-slate-600" data-id="element-17767">
            Бесплатный обзор или полный профессиональный разбор
          </p>
        </div>

        <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-17768">
          {/* Beta Card */}
          <motion.div variants={staggerItem} data-id="element-17769">
            <Card padding="lg" className="flex flex-col h-full border-slate-200" data-id="element-17770">
              <div className="flex items-center gap-3 mb-4" data-id="element-17771">
                <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center" data-id="element-17772">
                  <EyeIcon className="w-5 h-5" data-id="element-17773" />
                </div>
                <div data-id="element-17774">
                  <Badge variant="neutral" className="mb-1" data-id="element-17775">
                    Базовый
                  </Badge>
                  <h3 className="text-lg font-bold text-slate-900" data-id="element-17776">
                    Beta отчёт
                  </h3>
                </div>
              </div>
              <p className="text-sm text-slate-500 mb-4 flex-grow" data-id="element-17777">
                Краткий анализ — основные показатели и общие рекомендации для
                знакомства с услугой.
              </p>
              <ul className="space-y-2.5 mb-6" data-id="element-17778">
                {config.betaFeatures.map((feature, i) => <li key={i} className="flex items-start text-sm text-slate-600" data-id="element-17779">
                    <CheckIcon className="w-4 h-4 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-17780" />
                    {feature}
                  </li>)}
              </ul>
              <div className="pt-4 border-t border-slate-100" data-id="element-17781">
                <div className="flex items-baseline justify-between mb-4" data-id="element-17782">
                  <span className="text-2xl font-bold text-emerald-600" data-id="element-17783">
                    {config.betaPrice}
                  </span>
                  <span className="text-xs text-slate-400" data-id="element-17784">
                    {config.betaPages}
                  </span>
                </div>
                <Button variant="outline" fullWidth onClick={() => onNavigate(config.demoPage)} data-id="element-17785">
                  Посмотреть пример Beta
                </Button>
              </div>
            </Card>
          </motion.div>

          {/* Premium Card - Highlighted */}
          <motion.div variants={staggerItem} data-id="element-17786">
            <Card padding="lg" className={`flex flex-col h-full ${colors.ring} ring-1 shadow-xl relative`} data-id="element-17787">
              <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 z-10 ${colors.badge} text-white text-xs font-bold px-4 py-1 rounded-full`} data-id="element-17788">
                Рекомендуем
              </div>
              <div className="flex items-center gap-3 mb-4" data-id="element-17789">
                <div className={`w-10 h-10 rounded-xl ${colors.bg} ${colors.text} flex items-center justify-center`} data-id="element-17790">
                  <SparklesIcon className="w-5 h-5" data-id="element-17791" />
                </div>
                <div data-id="element-17792">
                  <Badge variant="pro" className="mb-1" data-id="element-17793">
                    Полный
                  </Badge>
                  <h3 className="text-lg font-bold text-slate-900" data-id="element-17794">
                    Premium отчёт
                  </h3>
                </div>
              </div>
              <p className="text-sm text-slate-500 mb-4 flex-grow" data-id="element-17795">
                Полный профессиональный отчёт с глубоким анализом и
                практическими рекомендациями.
              </p>
              <ul className="space-y-2.5 mb-6" data-id="element-17796">
                {config.premiumFeatures.map((feature, i) => <li key={i} className="flex items-start text-sm text-slate-700 font-medium" data-id="element-17797">
                    <CheckIcon className={`w-4 h-4 ${colors.text} mr-2 mt-0.5 flex-shrink-0`} data-id="element-17798" />
                    {feature}
                  </li>)}
              </ul>
              <div className="pt-4 border-t border-slate-100" data-id="element-17799">
                <div className="flex items-baseline justify-between mb-4" data-id="element-17800">
                  <span className="text-2xl font-bold text-slate-900" data-id="element-17801">
                    {config.premiumPrice}
                  </span>
                  <span className="text-xs text-slate-400" data-id="element-17802">
                    {config.premiumPages}
                  </span>
                </div>
                <Button variant="primary" fullWidth onClick={handleBuyPremium} className={`${colors.badge} hover:opacity-90`} data-id="element-17803">
                  Купить Premium отчёт
                  <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-17804" />
                </Button>
              </div>
            </Card>
          </motion.div>
        </motion.div>

        {/* Trust Badges */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500" data-id="element-17805">
          <span className="flex items-center" data-id="element-17806">
            <ShieldIcon className="w-4 h-4 mr-1.5 text-emerald-500" data-id="element-17807" />
            Безопасная оплата
          </span>
          <span className="flex items-center" data-id="element-17808">
            <ClockIcon className="w-4 h-4 mr-1.5 text-indigo-500" data-id="element-17809" />
            Мгновенная доставка
          </span>
          <span className="flex items-center" data-id="element-17810">
            <StarIcon className="w-4 h-4 mr-1.5 text-amber-500" data-id="element-17811" />
            Гарантия 14 дней
          </span>
        </div>
      </div>
    </div>;
}